import React from 'react';
import { CareProfilePlan } from 'data-types';
import * as translator from '../../../../../utils/translator';
import { formatDateStamp } from '../../../../../utils/timestampFormatter';
import checkIcon from '../../../../../images/CarePlus/check-icon.png';
import CareProfileCard from './CareProfileCard';
import { localeByLanguage } from '../careProfileAssets';
import {
	CARE_PROFILE_PLAN_FEATURE_KEYS,
	CareProfilePlanCardTier,
	isCareProfilePlanCardTier,
} from '../planFeatures';

interface CareProfilePlanCardProps {
	plan: CareProfilePlan;
	language: string;
}

const CareProfilePlanCard: React.FC<CareProfilePlanCardProps> = ({ plan, language }) => {
	if (!isCareProfilePlanCardTier(plan.tier)) {
		return null;
	}

	const tier = plan.tier as CareProfilePlanCardTier;
	const t = (path: string) => translator.translate(language, 'my_account', path);
	const dateLocale = localeByLanguage[language] || 'en-US';

	const nextRenewalLabel = plan.nextRenewal?.date
		? formatDateStamp(plan.nextRenewal.date, {
				noDayString: true,
				showYear: true,
				useShortMonth: true,
				locale: dateLocale,
			})
		: '–';

	const featureKeys = CARE_PROFILE_PLAN_FEATURE_KEYS[tier];

	return (
		<CareProfileCard id="care-profile-plan-card" className="border border-solid border-[#e7e4db]">
			<div className="flex flex-col gap-y-4 min-w-0">
				<div className="flex items-start justify-between gap-x-3">
					<p
						id="care-profile-plan-card-title"
						className="text-[11px] font-bold uppercase tracking-[1px] text-[#99a3b2] leading-[14px]"
					>
						{t('CareProfile/PlanCard/Title')}
					</p>
					<span
						id="care-profile-plan-card-tier-badge"
						className="text-[12px] font-medium text-[#e88103] bg-[#faf4ef] border border-solid border-[rgba(255,185,9,0.2)] rounded-full px-2.5 py-1 whitespace-nowrap"
					>
						{t(`CareProfile/EntryCard/Tier/${tier}`)}
					</span>
				</div>

				<div
					id="care-profile-plan-card-renewal-row"
					className="flex items-baseline justify-between gap-x-3"
				>
					<span className="text-[13px] text-[#616975] leading-[18px]">
						{t('CareProfile/PlanCard/NextRenewal')}
					</span>
					<span className="text-[14px] font-medium text-care-plus-heading leading-[18px] text-right">
						{nextRenewalLabel}
					</span>
				</div>

				<div className="h-px w-full bg-[#fdecd4]" />

				<div className="flex flex-col gap-y-2.5 min-w-0">
					<p
						id="care-profile-plan-card-includes-title"
						className="text-[13px] font-medium text-care-plus-heading leading-[18px]"
					>
						{t('CareProfile/EntryCard/IncludesTitle')}
					</p>
					<ul
						id="care-profile-plan-card-features"
						className="flex flex-col gap-y-2 m-0 p-0 list-none"
					>
						{featureKeys.map((key) => (
							<li
								key={key}
								className="flex items-start gap-x-2.5 text-[13px] text-[#08080a] leading-[18px]"
							>
								<span
									aria-hidden="true"
									className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-[#ffedd4]"
								>
									<img src={checkIcon} alt="" className="h-3 w-3" />
								</span>
								<span className="min-w-0 pt-0.5">{t(key)}</span>
							</li>
						))}
					</ul>
				</div>
			</div>
		</CareProfileCard>
	);
};

export default CareProfilePlanCard;
